import { create } from 'zustand';
import { useAuthStore } from './auth';
import { record, logBackgroundError } from '../observability';
import { onResume } from '../lib/resume';
import { getVapidKey, savePushSubscription, deletePushSubscription } from '../api/client';
import type { PushSubscriptionDto } from '../types';

// The push state of this device (#664, F37, bug 66).
//
// The device has ONE push state, so the whole app reads ONE store: the bell,
// the opt-in banner, the push-off line and the follow bell of a bout. Before,
// each caller of `usePushSubscription()` kept its own copy. A change through
// one caller did not reach the others, and `lib/boutBell.ts` subscribed the
// device and told nobody.
//
// This file holds the one subscribe sequence. The store (Enable) and
// `lib/boutBell.ts` (a tap on "Notify me") both run it under the one lock of
// the device. `hooks/usePushSubscription.ts` is a selector over the store.
// The release on sign-out stays in `logout()` in `api/client.ts`: it uses
// `fetch` on purpose, because an axios 401 would redirect to the sign-in page
// (#443).
//
// The store reads the device at the first load for each user. It reads the
// permission and the device again at each resume (`lib/resume.ts`, #499). The
// person can change the site permission in the browser settings, and the push
// service can drop a subscription. An installed app has no reload.
//
// `pushSupported` is false on a browser with no service worker or no
// PushManager (Safari before 16.4, most in-app browsers). `permission`
// mirrors Notification.permission: a 'denied' device never gets a prompt again.

const SW_PATH = '/sw.js';

export const pushSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

export type PushPermission = NotificationPermission | 'unsupported';

/** #557 — the result of an Enable. `denied`: the browser blocks push for
 *  this site. `error`: the thrown error, when there is one (an HTTP error of
 *  GET /vapid-key or POST /push-subscribe, the service worker, the push
 *  service). A server with no VAPID key gives no error. #664 — `busy`: the
 *  lock dropped the call, because an Enable or a Disable was already running.
 *  The call in flight shows its own result, so a `busy` result shows nothing. */
export type PushEnableResult =
  | { ok: true }
  | { ok: false; busy: true }
  | { ok: false; busy?: false; denied: boolean; error?: unknown };

/** #557 review — the result of a Disable. `error`: the thrown error (an HTTP
 *  error of DELETE /push-subscribe, the service worker, the push service).
 *  #664 — `busy`: the lock dropped the call, as for an Enable. */
export type PushDisableResult =
  | { ok: true }
  | { ok: false; busy: true }
  | { ok: false; busy?: false; error: unknown };

interface PushStore {
  enabled: boolean;
  /** #557 — the store reads the subscription of this device. Before that,
   *  `enabled` is false on every device, so an offer of push waits for this
   *  flag. */
  checked: boolean;
  /** An Enable or a Disable runs. */
  loading: boolean;
  permission: PushPermission;
  /** Read the subscription of this device for a signed-in user, or clear the
   *  state for `null`. It runs once for each user id. A resume uses another
   *  read, which keeps the state until the answer arrives. */
  check: (userId: string | null) => Promise<void>;
  enable: () => Promise<PushEnableResult>;
  disable: () => Promise<PushDisableResult>;
}

// #605, #664 — one Enable, Disable or subscribe at a time for the whole
// device. A call while one runs does nothing. Enable and Disable answer
// `{ ok: false, busy: true }`, and `subscribeDevice()` answers `false`. The
// call in flight shows its own result, so a caller shows nothing for a busy
// answer.
const lock = {
  held: false,
  async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    if (lock.held) return undefined;
    lock.held = true;
    usePushStore.setState({ loading: true });
    try {
      return await fn();
    } finally {
      lock.held = false;
      usePushStore.setState({ loading: false });
    }
  },
};

// The user id of the last `check()`. `undefined`: no check ran yet.
let checkedFor: string | null | undefined;
// The number of writes (Enable, Disable, a follow) that changed the
// subscription. A read of the device that started before a write must not
// replace it (#593).
let deviceWrites = 0;

// #443 — the subscription of this device follows the signed-in user. A session
// that ended without the sign-out button (an expiry, a closed tab) left the row
// with the previous user, and the bell showed "enabled" while that user's
// pushes arrived here. The POST moves the row: the backend deletes the row of
// any other user for the endpoint. Once for each user and page load.
let reboundFor: string | null = null;

function deviceBody(sub: PushSubscription): PushSubscriptionDto | null {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys) return null;
  return { endpoint: json.endpoint, keys: json.keys as PushSubscriptionDto['keys'] };
}

/** The one POST of this device to the backend. It answers `false` when the
 *  subscription has no endpoint or no keys, and then it sends nothing. */
async function saveDevice(sub: PushSubscription): Promise<boolean> {
  const body = deviceBody(sub);
  if (!body) return false;
  await savePushSubscription(body);
  return true;
}

function rebind(userId: string, sub: PushSubscription): void {
  if (reboundFor === userId) return;
  reboundFor = userId;
  saveDevice(sub)
    .then(sent => { if (!sent) reboundFor = null; })
    .catch(err => {
      // Reset the flag first: the next read of the device tries again.
      reboundFor = null;
      logBackgroundError('push:rebind')(err);
    });
}

/** Read the subscription of this device and show it in the store (#557, #593).
 *  A missing registration is normal: the device has no subscription. Any other
 *  error goes to telemetry, and the store keeps its state, because the read
 *  gave no answer. Ignore the answer when a newer user owns the store. Ignore
 *  it also when a write changed the subscription during the read: the write
 *  holds the newer state. */
async function readDevice(userId: string): Promise<void> {
  const writes = deviceWrites;
  let sub: PushSubscription | null = null;
  let failed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    sub = reg ? await reg.pushManager.getSubscription() : null;
  } catch (err) {
    failed = true;
    logBackgroundError('push:device-read')(err);
  }
  if (checkedFor !== userId) return;
  if (deviceWrites !== writes) { usePushStore.setState({ checked: true }); return; }
  if (failed) { usePushStore.setState({ checked: true }); return; }
  usePushStore.setState({ enabled: !!sub, checked: true });
  if (sub) rebind(userId, sub);
}

/** The subscribe sequence. It runs only under the lock of the device. */
async function subscribeSequence(): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH);
    const vapid = await getVapidKey();
    if (!vapid.data.publicKey) return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapid.data.publicKey,
    });
    await saveDevice(sub);
    deviceWrites++;
    usePushStore.setState({ enabled: true });
    return true;
  } finally {
    if ('Notification' in window) usePushStore.setState({ permission: Notification.permission });
  }
}

/**
 * Subscribe this device to Web Push and tell the backend: register the service
 * worker, read the VAPID key, subscribe, save the subscription. On success the
 * store holds `enabled`. The permission is read again at the end, also after
 * a failure, so a refusal at the prompt reaches every reader.
 *
 * It takes the lock of the device. When an Enable, a Disable or another
 * subscribe runs, it does nothing and answers `false`. The call in flight
 * shows its own result. It also answers `false` when the server has no VAPID
 * key. It throws every other failure and shows nothing: the caller decides
 * what the user reads. A tap on "Notify me" (`lib/boutBell.ts`) keeps the
 * refusal of the person silent. Enable (below) runs the sequence inside its
 * own lock and gives the error to its caller.
 */
export async function subscribeDevice(): Promise<boolean> {
  return (await lock.run(() => subscribeSequence())) ?? false;
}

export const usePushStore = create<PushStore>((set, get) => ({
  enabled: false,
  checked: false,
  loading: false,
  permission: pushSupported && 'Notification' in window ? Notification.permission : 'unsupported',

  check: async (userId) => {
    if (checkedFor === userId) return;
    checkedFor = userId;
    if (!userId || !pushSupported) { set({ enabled: false, checked: true }); return; }
    set({ enabled: false, checked: false });
    await readDevice(userId);
  },

  enable: async () => {
    if (!pushSupported) return { ok: false, denied: false };
    const result = await lock.run(async (): Promise<PushEnableResult> => {
      record({ event: 'push_optin_prompted', props: { action: 'enable' } });
      try {
        if (!(await subscribeSequence())) return { ok: false, denied: false };
        return { ok: true };
      } catch (error) {
        // The permission is denied, a request failed, or the push service
        // refused. The banner and the push-off line read `permission` to stop
        // asking on a denied device. #557 — the caller shows the reason: before,
        // a failed request showed nothing after a tap on "Enable".
        return { ok: false, denied: 'Notification' in window && Notification.permission === 'denied', error };
      }
    });
    return result ?? { ok: false, busy: true };
  },

  disable: async () => {
    if (!pushSupported) return { ok: true };
    const result = await lock.run(async (): Promise<PushDisableResult> => {
      record({ event: 'push_optin_prompted', props: { action: 'disable' } });
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
          await deletePushSubscription(sub.endpoint);
          await sub.unsubscribe();
        }
        deviceWrites++;
        set({ enabled: false });
        return { ok: true };
      } catch (error) {
        // #557 review — the DELETE or the unsubscribe failed, so the device
        // keeps its subscription: the toggle stays on. The caller shows the
        // reason. Before, an empty catch hid it, and a tap on the toggle showed
        // nothing. A sign-out does not come here: logout() in api/client.ts
        // releases the subscription on its own path, and it stays silent.
        return { ok: false, error };
      }
    });
    return result ?? { ok: false, busy: true };
  },
}));

// The store follows the signed-in user. A new user id starts one read of the
// device. A sign-out clears the state, so the next sign-in of any user reads
// the device again. The page load reads for the user that the browser holds.
useAuthStore.subscribe((state, prev) => {
  const id = state.user?.id ?? null;
  if (id !== (prev.user?.id ?? null)) void usePushStore.getState().check(id);
});
void usePushStore.getState().check(useAuthStore.getState().user?.id ?? null);

// A resume reads the permission and the device again (#664, #499). The read
// keeps `checked` and the current state until its answer arrives, so the
// banner does not flash (bug 66). A guest or a signed-out page does nothing.
onResume(() => {
  const userId = useAuthStore.getState().user?.id ?? null;
  if (!userId || !pushSupported || checkedFor !== userId) return;
  if ('Notification' in window) usePushStore.setState({ permission: Notification.permission });
  void readDevice(userId);
});
